import { IProductDetails } from '@models/IProductDetails';
import ProductStockStatusEnum from '@models/enums/ProductStockStatusEnum';
import ProductDetailsCta from '@molecules/ProductDetailsCta/ProductDetailsCta';
import ProductDetailsInformation from '@molecules/ProductDetailsInformation/ProductDetailsInformation';
import ProductDetailsPricing from '@molecules/ProductDetailsPricing/ProductDetailsPricing';
import ProductDetailsSummary from '@molecules/ProductDetailsSummary/ProductDetailsSummary';
import ProductVariationSelector from '@molecules/ProductVariationSelector/ProductVariationSelector';
import classNames from 'classnames';
import { RefObject } from 'react';

interface Props extends IProductDetails {
  readonly ctaContainerRef?: RefObject<HTMLDivElement>;
  readonly dataTestId?: string;
  readonly isProductDetailsLoading?: boolean;
  readonly onAddProductToCart: () => Promise<void>;
  readonly onGetProductData: (
    materialCode: string,
    updateUrl?: boolean
  ) => Promise<void>;
}

const ProductDetailsSidePanel = ({
  backOrderText,
  bannerNotification,
  buyNowLabel,
  buyNowUrl,
  categoriesAndOptions,
  commercialText,
  copyTaxLabel,
  ctaContainerRef,
  dataTestId = 'Product-Details-Side-Panel',
  dealerFindLink,
  deliveryInformation,
  deliveryLabel,
  disableFindDealerCta,
  ecoTaxLabel,
  isProductDetailsLoading,
  name,
  notifyMeContactFormEndpoint,
  preOrderText,
  pricingData,
  primarySkuCode,
  productImages,
  productInfo,
  refurbishedInformation,
  regionalTaxLabel,
  selectOptionLabel,
  shareOptions,
  skuCode,
  skuLabel,
  statusLabels,
  trackingData,
  usbCharger,
  usbChargerLabel,
  variations,
  yourSelectionLabel,
  onAddProductToCart,
  onGetProductData,
}: Props) => {
  const { status } = pricingData || {};
  const hasLimitedDisplay =
    !status ||
    status === ProductStockStatusEnum.DoNotShow ||
    status === ProductStockStatusEnum.BtoBOnly;
  const showVariationsSelector =
    !!categoriesAndOptions?.customisationCategories?.length ||
    (variations || []).length > 1;

  return (
    <div>
      <ProductDetailsSummary
        backOrderText={backOrderText}
        bannerNotification={bannerNotification}
        copyTaxLabel={copyTaxLabel}
        dataTestId={dataTestId}
        deliveryLabel={deliveryLabel}
        ecoTaxLabel={ecoTaxLabel}
        name={name}
        preOrderText={preOrderText}
        pricingData={pricingData}
        productInfo={productInfo}
        regionalTaxLabel={regionalTaxLabel}
        skuCode={skuCode}
        skuLabel={skuLabel}
        statusLabels={statusLabels}
        usbCharger={usbCharger}
        usbChargerLabel={usbChargerLabel}
        onAddProductToCart={onAddProductToCart}
      />

      {showVariationsSelector && (
        <div className="mb-2.5">
          <ProductVariationSelector
            categoriesAndOptions={categoriesAndOptions}
            currentVariation={
              variations?.find((item) => item.skuCode === skuCode) ??
              variations?.find((item) => item.skuCode === primarySkuCode)
            }
            dataTestId={`${dataTestId}-Variation-Selector`}
            isProductDetailsLoading={isProductDetailsLoading}
            mainProductImage={productImages[0]}
            selectOptionLabel={selectOptionLabel}
            variations={variations || []}
            yourSelectionLabel={yourSelectionLabel}
            onGetProductData={onGetProductData}
          />
        </div>
      )}

      {!hasLimitedDisplay && !!pricingData?.price && showVariationsSelector && (
        <div className="mb-7.5">
          <ProductDetailsPricing
            copyTaxLabel={copyTaxLabel}
            ecoTaxLabel={ecoTaxLabel}
            pricingData={pricingData}
            regionalTaxLabel={regionalTaxLabel}
            dataTestId={`${dataTestId}-Pricing-Bottom`}
          />
        </div>
      )}

      <div
        ref={ctaContainerRef}
        className={classNames({ 'mb-10 lg:mb-15': !hasLimitedDisplay })}
      >
        <ProductDetailsCta
          backOrderText={backOrderText}
          buyNowLabel={buyNowLabel}
          buyNowUrl={buyNowUrl}
          dataTestId={`${dataTestId}-CTA`}
          disableFindDealerCta={disableFindDealerCta}
          dealerFindLink={dealerFindLink}
          notifyMeContactFormEndpoint={notifyMeContactFormEndpoint}
          preOrderText={preOrderText}
          pricingData={pricingData}
          skuCode={skuCode}
          statusLabels={statusLabels}
          trackingData={trackingData}
          onAddProductToCart={onAddProductToCart}
        />
      </div>

      {!hasLimitedDisplay && (
        <ProductDetailsInformation
          commercialText={commercialText}
          deliveryInformation={deliveryInformation}
          refurbishedInformation={refurbishedInformation}
          shareOptions={shareOptions}
          dataTestId={`${dataTestId}-Information`}
        />
      )}
    </div>
  );
};

export default ProductDetailsSidePanel;
